<template>
	<section class="page-content-slim">
		<div class="container">
			<div class="row">
				<div class="col-xs-12 col-sm-8">
					<h1>IADSA SUPPLEMENT REGULATION DATABASE</h1>
					<div class="page-summary-container">
						<div class="page-summary-text">
							<template v-if="searchTerm">
								{{ `Searching for ${searchTerm}` }}
								<b-btn class="btn-danger pull-right" @click="setSearchTerm(null); search();">
									<i class="material-icons">cancel</i>
								</b-btn>
							</template>
						</div>
					</div>
				</div>
				<div class="col-xs-6 col-sm-3 col-sm-offset-1">
					<b-btn
						class="btn btn-primary btn-md btn-block btn-filter"
						data-toggle="collapse"
						aria-expanded="false"
						@click="showFilters=!showFilters"
					>
					Filter
					</b-btn>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12">
					<filtering
						:showFilters="showFilters"
						:filter-options="filterOptions"
						:sort-options="sortOptions"
						:selected-filters="selectedFilters"
						@resetSearchTerm="setSearchTerm(null)"
						@selectedOptions="applyFilters"
					/>
				</div>
			</div>
			<template v-if="loading">
				<div class="row">
					<div class="col-xs-12">
						<div class="container loading-container">
							<img class="section-loader" src="@/assets/images/iadsa-section-loader-admin.gif"/>
						</div>
					</div>
				</div>
			</template>
			<template v-else-if="regulations && regulations.length > 0">
				<div
					v-for="regulation in regulations"
					:key="regulation.id"
					class="list-container list-container-notifications"
				>
					<div class="row">
						<div class="col-xs-12 col-sm-4 col-md-2 col-lg-2">
							<template v-if="regulation.regions.length > 0" >
								<div class="sm-padding-bottom-10">
									<b-btn
										v-for="region in regulation.regions"
										:key="region.id"
										class="label label-red"
										@click="appendAndApplyFilter('regions', region.id)"
									>
										&nbsp;{{ region.name }}&nbsp;
									</b-btn>
								</div>
							</template>
							<template v-else>
								<div class="sm-padding-bottom-10">
									<span class="label label-default">No regions set</span>
								</div>
							</template>
						</div>
						<div class="col-xs-12 col-sm-8 col-md-2 col-lg-2">
							<div class="sm-padding-bottom-10">
								<div class="no-margin">{{ formatStartDate(regulation.start_date) }}</div>
								<div class="no-margin">{{ `Ref: ${regulation.reference}` }}</div>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-5 col-lg-5">
							<div class="sm-padding-bottom-10">
								<div class="notification-list-heading">
									<router-link
										:to="`/resource/${regulation.id}`"
									>
										{{ regulation.title }}
									</router-link>
								</div>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-3 col-lg-3">
							<div class="sm-padding-bottom-10">
								<b-btn
									class="label label-status"
									@click="appendAndApplyFilter('status', regulation.status.id)"
								>{{ regulation.status.name }}</b-btn>
								<br/>
								<b-btn
									v-for="tag in regulation.tags"
									:key="tag.id"
									class="label label-gray"
									@click="appendAndApplyFilter('tags', tag.id)"
								>
									&nbsp;{{ tag.name }}&nbsp;
								</b-btn>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="row">
					<div class="col-xs-12">
						<div class="container center">
							<h2>No results found</h2>
						</div>
					</div>
				</div>
			</template>
			<div class="row">
				<div class="col-xs-12">
					<div class="pagination-container">
						<b-pagination
							v-if="pagination && pagination.totalPages > 1"
							v-model="pagination.page"
							:total-page="pagination.totalPages"
						/>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style lang="less" scoped>
	.input-group {
		margin-bottom: 30px;
	}
	.btn-primary {
		height: 40.25px;
	}
	.loading-container {
		text-align: center;
	}
	.section-loader {
		max-width: 200px;
		height: auto;
		margin: 30px auto;
	}
</style>

<script>
import moment from 'moment';
import filtering from '@/components/filtering';
import { map, isEmpty, isNull, flow, filter, orderBy } from 'lodash/fp';

export default {
	components: { filtering },
	created() {
		this.setPreSelectedFilters();
		this.getRegulations();
		this.getRegulationFilterOptions();
	},
	data() {
		return {
			showFilters: false,
			regulations: null,
			filterOptions: {},
			sortOptions: [],
			pagination: {
				page: 1
			},
			searchTerm: null,
			selectedSortOrder: null,
			selectedFilters: {
				regions: [],
				status: [],
				dates: [],
				tags: []
			},
			loading: false
		};
	},
	computed: {
		getSearchTermFromQuery() {
			return this.$route.query.searchTerm;
		}
	},
	methods: {
		setPreSelectedFilters() {
			if (!isEmpty(this.$route.query.selectedFilters)) {
				this.$set(this, 'selectedFilters', this.$route.query.selectedFilters);
			}
			if (!isNull(this.$route.query.searchTerm)) {
				this.setSearchTerm(this.$route.query.searchTerm);
			}
		},
		formatStartDate(startDate) {
			return moment(startDate).format('DD/MM/YYYY');
		},
		async getRegulations(params) {
			this.loading = true;
			const { searchTerm, selectedSortOrder, selectedFilters } = this;

			const { data: { data: regulations, pagination } } = await this.$axios.get('/resource', {
				params: {
					searchTerm,
					sortBy: selectedSortOrder,
					page: this.pagination.page,
					...selectedFilters,
					...params
				}
			});
			this.$set(this, 'regulations', regulations);
			this.$set(this, 'pagination', pagination);
			this.loading = false;
		},
		async getRegulationFilterOptions() {
			const { data: { data: options } } = await this.$axios.get('/resource/filter-options');

			const formattedSortOptions = map(({ code, name }) => ({
				value: code, label: name
			}), options.sortOptions);

			const formattedRegions = flow(
				map(({ id, name }) => ({
					value: id,
					label: name
				})),
				orderBy('label', 'asc')
			)(options.filterOptions.regions);

			const formattedStatus = map(({ id, name }) => ({
				value: id, label: name
			}), options.filterOptions.status);

			const formattedDates = flow(
				filter(value => value !== 'Invalid date'),
				map(value => ({ value, label: value	}))
			)(options.filterOptions.dates);

			const formattedTags = map(({ id, name }) => ({
				value: id, label: name
			}), options.filterOptions.tags);

			this.$set(this, 'sortOptions', formattedSortOptions);
			this.$set(this, 'filterOptions', {
				regions: formattedRegions,
				status: formattedStatus,
				dates: formattedDates,
				tags: formattedTags
			});
		},
		async applyFilters(options) {
			this.$set(this, 'selectedSortOrder', options.selectedSortOrder);
			this.$set(this, 'selectedFilters', options.selectedFilters);
			await this.getRegulations({ page: 1 });
		},
		async search() {
			await this.getRegulations({ page: 1 });
		},
		setSearchTerm(term) {
			this.$set(this, 'searchTerm', term);
		},
		resetFilters() {
			this.selectedFilters = {
				regions: [],
				status: [],
				dates: [],
				tags: []
			};
		},
		async appendAndApplyFilter(type, id) {
			this.resetFilters();
			this.$set(this.selectedFilters, type, [id]);
			await this.getRegulations({ page: 1 });
		}
	},
	watch: {
		'pagination.page': async function pageWatcher() {
			await this.getRegulations();
		},
		'$route.query.searchTerm': async function searchTermWatcher() {
			if (!isNull(this.$route.query.searchTerm)) {
				this.setSearchTerm(this.$route.query.searchTerm);
				await this.search();
			} else {
				await this.getRegulations();
			}
		}
	},
};
</script>
